import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  BarChart3,
  BookOpenText,
  Calculator,
  ChevronLeft,
  ChevronRight,
  FileText,
  LucideIcon,
  Users,
  X,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ScreenRenderer from '../components/sim/ScreenRenderer';
import SimScreenSkeleton from '../components/SimScreenSkeleton';
import SimButton from '../components/ui/SimButton';
import api from '../services/api';
import SimCard from '../sim/ui/SimCard';
import SimCalculator from '../sim/ui/SimCalculator';
import SimShell from '../sim/ui/SimShell';
import { SimBody, SimLabel, SimTitle } from '../sim/ui/SimText';

type RuntimeScreen = {
  id: string;
  screenCode: string;
  screenName: string;
  screenOrder: number;
  timeLimitSeconds: number;
  ui?: {
    show_calculator: boolean;
    lock_back_navigation: boolean;
  };
  components: Array<{
    rowKey: string;
    rowOrder: number;
    rowType: string;
    payload: Record<string, unknown>;
  }>;
};

type RuntimeReferenceTab = {
  id: string;
  title: string;
  contentMarkdown: string;
  required: boolean;
};

type RuntimeAttemptBundle = {
  simulation: {
    id: string;
    name: string;
    roleFamily: string;
    version: string;
  };
  variant: {
    id: string;
    variantCode: string;
    variantName: string;
  };
  attempt: {
    id: string;
    status: string;
    started_at: string;
    scored_started_at: string | null;
    current_screen_code?: string;
    watermark: {
      full_name: string;
      sa_id_last4: string;
    };
  };
  progress: {
    current: number;
    total: number;
  };
  totalTimeSeconds: number;
  referenceTabs: RuntimeReferenceTab[];
  tabs?: RuntimeReferenceTab[] | { referenceTabs?: RuntimeReferenceTab[] };
  openedTabKeys?: string[];
  opened_tab_keys?: string[];
  tutorialConfig?: {
    introMarkdown?: string;
    config?: Record<string, unknown>;
  } | null;
  screens: RuntimeScreen[];
};

type SubmitScreenResponse = {
  status: string;
  next_url: string | null;
  already_submitted?: boolean;
  expired?: boolean;
};

type SimAttemptEvent = {
  ts: number;
  name: string;
  props?: Record<string, unknown>;
};

type TutorialConfig = {
  introMarkdown: string;
  buckets: string[];
  bucketItems: Array<{ id: string; label: string; correctBucket: string }>;
  numericPrompt: string;
  numericExpected: number;
  numericTolerance: number;
  multiSelectPrompt: string;
  multiSelectSelectExactly: number;
  multiSelectStatements: Array<{ id: string; text: string; incorrect: boolean }>;
};

export type TutorialProgressState = {
  calculatorOpened: boolean;
  allReferenceTabsOpened: boolean;
  bucketSortComplete: boolean;
  numericCorrect: boolean;
  multiSelectCorrect: boolean;
};

export type ReferenceTabTutorialStatus = {
  total: number;
  opened: number;
  required: boolean;
  satisfiedForCompletion: boolean;
  checklistComplete: boolean;
};

const MAX_EVENTS_PER_SCREEN = 300;
const IDLE_THRESHOLD_MS = 30_000;
const BURST_WINDOW_MS = 10_000;
const attemptRuntimeCache = new Map<string, RuntimeAttemptBundle>();
const REFERENCE_SECTION_TINT_CLASSES = ['bg-brand-600/10', 'bg-copper-700/10', 'bg-accent-indigo/10', 'bg-danger/10'];
const REFERENCE_SECTION_ICONS: LucideIcon[] = [FileText, BarChart3, Users, AlertTriangle];

export const getAttemptRuntimeCacheKey = (attemptId: string, variantId?: string): string =>
  `${attemptId}:${variantId || ''}`;

const DEFAULT_TUTORIAL_CONFIG: TutorialConfig = {
  introMarkdown:
    'Complete this short tutorial before the scored assessment begins. Open the calculator, open each reference tab, and complete all 3 practice tasks.',
  buckets: ['Animals', 'Numbers', 'Objects'],
  bucketItems: [
    { id: 'dog', label: 'Dog', correctBucket: 'Animals' },
    { id: 'seven', label: '7', correctBucket: 'Numbers' },
    { id: 'chair', label: 'Chair', correctBucket: 'Objects' },
  ],
  numericPrompt: 'Compute 420 / 3',
  numericExpected: 140,
  numericTolerance: 0,
  multiSelectPrompt: 'Select exactly 2 incorrect statements.',
  multiSelectSelectExactly: 2,
  multiSelectStatements: [
    { id: 's1', text: 'A triangle has three sides.', incorrect: false },
    { id: 's2', text: '2 + 2 equals 4.', incorrect: false },
    { id: 's3', text: 'A week has 7 days.', incorrect: false },
    { id: 's4', text: 'The opposite of “up” is “down”.', incorrect: false },
    { id: 's5', text: 'The sun rises in the east.', incorrect: false },
    { id: 's6', text: 'Water can exist as ice, liquid, or steam.', incorrect: false },
    { id: 's7', text: 'The letter A comes before B in the alphabet.', incorrect: false },
    { id: 's8', text: 'Most people have two eyes.', incorrect: false },
    { id: 's9', text: 'Most foxes have 5 legs and are partial to cigars.', incorrect: true },
    { id: 's10', text: 'Every frog likes to swim in and drink good whiskey.', incorrect: true },
  ],
};

export const isTutorialComplete = (state: TutorialProgressState): boolean =>
  state.calculatorOpened &&
  state.allReferenceTabsOpened &&
  state.bucketSortComplete &&
  state.numericCorrect &&
  state.multiSelectCorrect;

export const computeRemainingSeconds = (
  scoredStartedAtMs: number | null,
  totalTimeSeconds: number,
  nowMs: number
): number => {
  if (!scoredStartedAtMs) return Math.max(0, totalTimeSeconds);
  const elapsed = Math.max(0, Math.floor((nowMs - scoredStartedAtMs) / 1000));
  return Math.max(0, totalTimeSeconds - elapsed);
};

export const computeReferenceTabTutorialStatus = (
  referenceTabIds: string[],
  openedReferenceTabIds: Set<string>
): ReferenceTabTutorialStatus => {
  const total = referenceTabIds.length;
  const opened = referenceTabIds.reduce(
    (count, id) => (openedReferenceTabIds.has(id) ? count + 1 : count),
    0
  );
  const required = total > 0;
  const allOpened = required && opened >= total;
  return {
    total,
    opened,
    required,
    satisfiedForCompletion: required ? allOpened : true,
    checklistComplete: allOpened,
  };
};

export const formatScreenTitle = (
  screenCode: string | undefined,
  displayName: string | undefined,
  index: number
): string => {
  const normalizedCode = asString(screenCode, '');
  const rawName = asString(displayName, normalizedCode || 'Screen');
  const stripCodePrefix = (() => {
    if (!normalizedCode) return rawName;
    const escapedCode = normalizedCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return rawName.replace(new RegExp(`^${escapedCode}[_:\\-\\s]*`, 'i'), '').trim();
  })();
  const stripIndexedPrefix = stripCodePrefix.replace(/^(?:[Ss]creen\s*\d+|[Ss]\d+)[_:\-\s]*/g, '').trim();
  const humanName = asString(stripIndexedPrefix, asString(stripCodePrefix, 'Screen'));
  const screenIndex = Number.isFinite(index) && Number.isInteger(index) && index > 0 ? index : 1;
  return `Screen ${screenIndex} ${humanName}`;
};

const asNumber = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : fallback;

const asStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const result: string[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    const parsed = typeof item === 'string' ? item.trim() : '';
    if (!parsed || seen.has(parsed)) continue;
    seen.add(parsed);
    result.push(parsed);
  }
  return result;
};

const parseCandidateNumericInput = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const direct = Number(trimmed);
  if (Number.isFinite(direct)) return direct;

  let normalized = trimmed;
  let isNegative = false;
  if (/^\(.*\)$/.test(normalized)) {
    isNegative = true;
    normalized = normalized.slice(1, -1);
  }

  normalized = normalized
    .replace(/\s+/g, '')
    .replace(/[^\d,.\-]/g, '')
    .replace(/(?!^)-/g, '');

  if (!normalized) return null;

  const hasComma = normalized.includes(',');
  const hasDot = normalized.includes('.');
  if (hasComma && hasDot) {
    if (normalized.lastIndexOf(',') > normalized.lastIndexOf('.')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }
  } else if (hasComma && !hasDot) {
    const lastComma = normalized.lastIndexOf(',');
    const decimalDigits = normalized.length - lastComma - 1;
    if (decimalDigits > 0 && decimalDigits <= 2) {
      normalized = normalized.replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return null;
  return isNegative ? -parsed : parsed;
};

export const useScrollGate = (
  scrollContainerRef: React.RefObject<HTMLElement | null>,
  screenCode: string | undefined | null,
  threshold = 0.9,
  enabled = true
): boolean => {
  const [satisfiedScreens, setSatisfiedScreens] = useState<Record<string, boolean>>({});
  const key = screenCode || '';
  const isSatisfied = key ? Boolean(satisfiedScreens[key]) : true;

  useEffect(() => {
    if (!enabled || !key) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const evaluate = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const maxScroll = scrollHeight - clientHeight;
      if (clientHeight > 0 && maxScroll <= 0) {
        setSatisfiedScreens((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
        return;
      }

      const reachedRatio = scrollTop / maxScroll;
      if (reachedRatio >= threshold) {
        setSatisfiedScreens((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
      }
    };

    evaluate();
    const onScroll = () => {
      evaluate();
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', onScroll);
    };
  }, [enabled, key, screenCode, scrollContainerRef, threshold]);

  return isSatisfied;
};

const normalizeBucketSortItemKeys = (value: unknown): string[] => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  const payload = value as Record<string, unknown>;
  const rawItems = payload.items;
  const rawCorrectAssignments =
    payload.correctAssignments && typeof payload.correctAssignments === 'object' && !Array.isArray(payload.correctAssignments)
      ? (payload.correctAssignments as Record<string, unknown>)
      : payload.correct && typeof payload.correct === 'object' && !Array.isArray(payload.correct)
        ? (payload.correct as Record<string, unknown>)
        : null;
  if (!Array.isArray(rawItems)) {
    if (!rawCorrectAssignments) return [];
    return Object.keys(rawCorrectAssignments).map((key) => key.trim()).filter(Boolean);
  }
  const seen = new Set<string>();
  const keys: string[] = [];
  for (const item of rawItems) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      const key = typeof item === 'string' ? item.trim() : '';
      if (!key || seen.has(key)) continue;
      seen.add(key);
      keys.push(key);
      continue;
    }
    const record = item as Record<string, unknown>;
    const key = asString(record.key, asString(record.id));
    if (!key || seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }
  return keys;
};

const normalizeBucketSortBucketKeys = (value: unknown): string[] => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  const payload = value as Record<string, unknown>;
  const rawBuckets = payload.buckets;
  const seen = new Set<string>();
  const keys: string[] = [];

  if (Array.isArray(rawBuckets)) {
    for (const bucket of rawBuckets) {
      if (!bucket || typeof bucket !== 'object' || Array.isArray(bucket)) {
        const key = typeof bucket === 'string' ? bucket.trim() : '';
        if (!key || seen.has(key)) continue;
        seen.add(key);
        keys.push(key);
        continue;
      }
      const record = bucket as Record<string, unknown>;
      const key = asString(record.key, asString(record.id));
      if (!key || seen.has(key)) continue;
      seen.add(key);
      keys.push(key);
    }
  }

  if (keys.length > 0) return keys;

  const rawCorrectAssignments =
    payload.correctAssignments && typeof payload.correctAssignments === 'object' && !Array.isArray(payload.correctAssignments)
      ? (payload.correctAssignments as Record<string, unknown>)
      : payload.correct && typeof payload.correct === 'object' && !Array.isArray(payload.correct)
        ? (payload.correct as Record<string, unknown>)
        : null;
  if (!rawCorrectAssignments) return [];
  for (const value of Object.values(rawCorrectAssignments)) {
    const key = typeof value === 'string' ? value.trim() : '';
    if (!key || seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }
  return keys;
};

const normalizeMatchingPairLeftItemKeys = (value: unknown): string[] => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  const payload = value as Record<string, unknown>;
  const leftItems = Array.isArray(payload.leftItems) ? payload.leftItems : [];
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const item of leftItems) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const record = item as Record<string, unknown>;
    const key = asString(record.key, asString(record.id));
    if (!key || seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }
  return keys;
};

type RequiredTaskDescriptor = {
  rowKey: string;
  taskType: string;
  selectExactly: number | null;
  minSelect: number | null;
  pairExactly: number | null;
  matchingLeftItemKeys: string[];
  bucketItemKeys: string[];
  bucketKeys: string[];
  requireAllAssigned: boolean;
};

export const isRequiredTaskComplete = (task: RequiredTaskDescriptor, value: unknown): boolean => {
  if (task.taskType === 'NumericEntry') {
    return parseCandidateNumericInput(value) !== null;
  }
  if (task.taskType === 'SingleChoice' || task.taskType === 'Picklist') {
    if (value === undefined || value === null) return false;
    return String(value).trim().length > 0;
  }
  if (task.taskType === 'MultiSelect') {
    const selected = Array.isArray(value) ? value.map((v) => String(v).trim()).filter(Boolean) : [];
    if (task.selectExactly) return selected.length === task.selectExactly;
    if (task.minSelect) return selected.length >= task.minSelect;
    return selected.length > 0;
  }
  if (task.taskType === 'MatchingPairs') {
    const pairs =
      value && typeof value === 'object' && !Array.isArray(value) && Array.isArray((value as { pairs?: unknown }).pairs)
        ? ((value as { pairs: unknown[] }).pairs as unknown[])
        : [];
    const normalizedPairs = pairs
      .map((pair) => {
        if (!pair || typeof pair !== 'object' || Array.isArray(pair)) return null;
        const record = pair as Record<string, unknown>;
        const leftId = asString(record.leftId);
        const rightId = asString(record.rightId);
        if (!leftId || !rightId) return null;
        return { leftId, rightId };
      })
      .filter((pair): pair is { leftId: string; rightId: string } => Boolean(pair));
    if (task.pairExactly) return normalizedPairs.length === task.pairExactly;
    if (task.matchingLeftItemKeys.length === 0) return normalizedPairs.length > 0;
    const pairedLeftIds = new Set(normalizedPairs.map((pair) => pair.leftId));
    return task.matchingLeftItemKeys.every((leftKey) => pairedLeftIds.has(leftKey));
  }
  if (task.taskType === 'BucketSort') {
    if (!task.requireAllAssigned) return true;
    if (!task.bucketItemKeys.length || !task.bucketKeys.length) return false;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

    const validBucketKeys = new Set(task.bucketKeys);
    const assignedItemKeys = Object.entries(value as Record<string, unknown>)
      .filter(
        ([, bucketKey]) =>
          typeof bucketKey === 'string' &&
          bucketKey.trim().length > 0 &&
          validBucketKeys.has(bucketKey.trim())
      )
      .map(([itemKey]) => itemKey.trim())
      .filter(Boolean);
    if (assignedItemKeys.length < task.bucketItemKeys.length) return false;
    const assignedSet = new Set(assignedItemKeys);
    return task.bucketItemKeys.every((itemKey) => assignedSet.has(itemKey));
  }
  return true;
};

export const hasMissingRequiredTaskAnswers = (
  requiredTasks: RequiredTaskDescriptor[],
  answers: Record<string, unknown>,
  hasTaskInputs: boolean
): boolean => {
  if (!hasTaskInputs || requiredTasks.length === 0) return false;
  return requiredTasks.some((task) => !isRequiredTaskComplete(task, answers[task.rowKey]));
};

export const computeScreenProgress = (
  screens: Array<{ screenCode: string }>,
  screenCode: string | undefined
): { current: number; total: number } => {
  const total = screens.length;
  if (total === 0) return { current: 1, total: 0 };
  const index = screens.findIndex((screen) => screen.screenCode === screenCode);
  return {
    current: index >= 0 ? index + 1 : 1,
    total,
  };
};

const getRuntimeCacheEntryForAttempt = (attemptId: string, variantId?: string): { key: string; value: RuntimeAttemptBundle } | null => {
  const preferredKey = getAttemptRuntimeCacheKey(attemptId, variantId);
  const direct = attemptRuntimeCache.get(preferredKey);
  if (direct) return { key: preferredKey, value: direct };
  return null;
};

const clearAttemptRuntimeCache = (attemptId: string, variantId?: string): void => {
  const entry = getRuntimeCacheEntryForAttempt(attemptId, variantId);
  if (entry) {
    attemptRuntimeCache.delete(entry.key);
  }
};

const setAttemptRuntimeCache = (
  attemptId: string,
  variantId: string | undefined,
  bundle: RuntimeAttemptBundle
): void => {
  attemptRuntimeCache.set(getAttemptRuntimeCacheKey(attemptId, variantId), bundle);
};

export const detectReferenceTabShape = (
  runtime: RuntimeAttemptBundle | null
): 'referenceTabs' | 'tabs-array' | 'tabs-referenceTabs' | 'none' => {
  if (!runtime) return 'none';
  if (Array.isArray(runtime.referenceTabs) && runtime.referenceTabs.length > 0) return 'referenceTabs';
  if (Array.isArray(runtime.tabs) && runtime.tabs.length > 0) return 'tabs-array';
  if (
    runtime.tabs &&
    typeof runtime.tabs === 'object' &&
    Array.isArray(runtime.tabs.referenceTabs) &&
    runtime.tabs.referenceTabs.length > 0
  ) {
    return 'tabs-referenceTabs';
  }
  return 'none';
};

export const getReferenceTabs = (runtime: RuntimeAttemptBundle | null): RuntimeReferenceTab[] => {
  if (!runtime) return [];
  if (Array.isArray(runtime.referenceTabs) && runtime.referenceTabs.length > 0) return runtime.referenceTabs;
  if (Array.isArray(runtime.tabs)) return runtime.tabs;
  if (runtime.tabs && typeof runtime.tabs === 'object' && Array.isArray(runtime.tabs.referenceTabs)) {
    return runtime.tabs.referenceTabs;
  }
  return [];
};

export const getOpenedTabKeys = (runtime: RuntimeAttemptBundle | null): string[] => {
  if (!runtime) return [];
  if (Array.isArray(runtime.openedTabKeys)) return asStringArray(runtime.openedTabKeys);
  if (Array.isArray(runtime.opened_tab_keys)) return asStringArray(runtime.opened_tab_keys);
  return [];
};

const buildTutorialConfig = (runtime: RuntimeAttemptBundle | null): TutorialConfig => {
  const override = runtime?.tutorialConfig?.config || {};
  const introMarkdown =
    asString(runtime?.tutorialConfig?.introMarkdown) ||
    asString(override.introMarkdown, DEFAULT_TUTORIAL_CONFIG.introMarkdown);

  const buckets = Array.isArray(override.buckets)
    ? override.buckets.map((item) => asString(item)).filter(Boolean)
    : DEFAULT_TUTORIAL_CONFIG.buckets;

  const bucketItems = Array.isArray(override.bucketItems)
    ? override.bucketItems
        .map((raw) => {
          if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
          const row = raw as Record<string, unknown>;
          const id = asString(row.id);
          const label = asString(row.label);
          const correctBucket = asString(row.correctBucket);
          if (!id || !label || !correctBucket) return null;
          return { id, label, correctBucket };
        })
        .filter((item): item is { id: string; label: string; correctBucket: string } => Boolean(item))
    : DEFAULT_TUTORIAL_CONFIG.bucketItems;

  const statements = Array.isArray(override.multiSelectStatements)
    ? override.multiSelectStatements
        .map((raw, index) => {
          if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
          const row = raw as Record<string, unknown>;
          const id = asString(row.id, `s${index + 1}`);
          const text = asString(row.text);
          const incorrect = Boolean(row.incorrect);
          if (!text) return null;
          return { id, text, incorrect };
        })
        .filter((item): item is { id: string; text: string; incorrect: boolean } => Boolean(item))
    : DEFAULT_TUTORIAL_CONFIG.multiSelectStatements;

  return {
    introMarkdown,
    buckets: buckets.length > 0 ? buckets : DEFAULT_TUTORIAL_CONFIG.buckets,
    bucketItems: bucketItems.length > 0 ? bucketItems : DEFAULT_TUTORIAL_CONFIG.bucketItems,
    numericPrompt: asString(override.numericPrompt, DEFAULT_TUTORIAL_CONFIG.numericPrompt),
    numericExpected: asNumber(override.numericExpected, DEFAULT_TUTORIAL_CONFIG.numericExpected),
    numericTolerance: Math.max(0, asNumber(override.numericTolerance, DEFAULT_TUTORIAL_CONFIG.numericTolerance)),
    multiSelectPrompt: asString(override.multiSelectPrompt, DEFAULT_TUTORIAL_CONFIG.multiSelectPrompt),
    multiSelectSelectExactly: Math.max(
      1,
      Math.floor(asNumber(override.multiSelectSelectExactly, DEFAULT_TUTORIAL_CONFIG.multiSelectSelectExactly))
    ),
    multiSelectStatements: statements.length > 0 ? statements : DEFAULT_TUTORIAL_CONFIG.multiSelectStatements,
  };
};

type TutorialPanelProps = {
  config: TutorialConfig;
  calculatorOpened: boolean;
  referenceTabIds: string[];
  openedReferenceTabIds: Set<string>;
  onStart: () => void;
  starting: boolean;
};

const TutorialPanel: React.FC<TutorialPanelProps> = ({
  config,
  calculatorOpened,
  referenceTabIds,
  openedReferenceTabIds,
  onStart,
  starting,
}) => {
  const [dragItemId, setDragItemId] = useState<string>('');
  const [bucketAssignments, setBucketAssignments] = useState<Record<string, string>>({});
  const [numericAnswer, setNumericAnswer] = useState('');
  const [multiSelectAnswer, setMultiSelectAnswer] = useState<string[]>([]);

  const incorrectIds = useMemo(
    () => new Set(config.multiSelectStatements.filter((statement) => statement.incorrect).map((statement) => statement.id)),
    [config.multiSelectStatements]
  );
  const referenceTabStatus = computeReferenceTabTutorialStatus(referenceTabIds, openedReferenceTabIds);
  const bucketSortComplete = config.bucketItems.every((item) => bucketAssignments[item.id] === item.correctBucket);
  const numericValue = parseCandidateNumericInput(numericAnswer);
  const numericCorrect =
    numericAnswer.trim().length > 0 &&
    numericValue !== null &&
    Math.abs(numericValue - config.numericExpected) <= config.numericTolerance;
  const selectedSet = new Set(multiSelectAnswer);
  const multiSelectCorrect =
    multiSelectAnswer.length === config.multiSelectSelectExactly &&
    selectedSet.size === incorrectIds.size &&
    [...selectedSet].every((id) => incorrectIds.has(id));

  const tutorialComplete =
    isTutorialComplete({
      calculatorOpened,
      allReferenceTabsOpened: referenceTabStatus.satisfiedForCompletion,
      bucketSortComplete,
      numericCorrect,
      multiSelectCorrect,
    });

  return (
    <div className="w-full">
      <SimCard className="border-0 bg-transparent p-0 shadow-none">
        <div className="space-y-6">
          <div className="rounded-input border border-line bg-surface-header p-4">
            <p className="text-xs uppercase tracking-[0.14em] text-txt-muted">Orientation checklist</p>
            <div className="mt-3 space-y-2 text-sm text-txt-secondary">
              <p>{calculatorOpened ? '✅' : '⬜'} Open calculator once</p>
              {referenceTabStatus.required ? (
                <p>
                  {referenceTabStatus.checklistComplete ? '✅' : '⬜'} Open each reference tab once ({referenceTabStatus.opened} /{' '}
                  {referenceTabStatus.total} tabs)
                </p>
              ) : (
                <p>ℹ️ No reference tabs configured for this simulation.</p>
              )}
            </div>
            <p className="mt-3 text-xs uppercase tracking-[0.18em] text-txt-muted">
              Open references: {referenceTabStatus.opened} / {referenceTabStatus.total}
            </p>
          </div>

          <div className="space-y-3 rounded-input border border-line bg-surface-header p-4">
            <p className="text-xs uppercase tracking-[0.14em] text-txt-muted">Practice 1: Classify 3 facts into buckets</p>
            <div className="flex flex-wrap gap-2">
              {config.bucketItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  draggable
                  onDragStart={() => setDragItemId(item.id)}
                  className="rounded-input border border-line bg-surface-card px-3 py-1.5 text-sm text-txt-primary"
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {config.buckets.map((bucket) => (
                <div
                  key={bucket}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (!dragItemId) return;
                    setBucketAssignments((prev) => ({ ...prev, [dragItemId]: bucket }));
                    setDragItemId('');
                  }}
                  className="min-h-24 rounded-input border border-line bg-surface-card/70 p-3"
                >
                  <p className="text-xs uppercase tracking-[0.12em] text-txt-muted">{bucket}</p>
                  <div className="mt-2 space-y-1">
                    {config.bucketItems
                      .filter((item) => bucketAssignments[item.id] === bucket)
                      .map((item) => (
                        <p key={`${bucket}:${item.id}`} className="rounded bg-brand-700 px-2 py-1 text-sm text-txt-inverse">
                          {item.label}
                        </p>
                      ))}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm text-txt-muted">{bucketSortComplete ? '✅ Correct' : 'Arrange all 3 items correctly.'}</p>
          </div>

          <div className="space-y-3 rounded-input border border-line bg-surface-header p-4">
            <p className="text-xs uppercase tracking-[0.14em] text-txt-muted">Practice 2: Numeric entry using calculator</p>
            <p className="text-sm text-txt-primary">{config.numericPrompt}</p>
            <input
              type="text"
              inputMode="decimal"
              value={numericAnswer}
              onChange={(event) => setNumericAnswer(event.target.value)}
              className="w-full rounded-input border border-line bg-surface-card px-3 py-2 text-txt-primary"
              placeholder="Enter answer"
            />
            <p className="text-sm text-txt-muted">{numericCorrect ? '✅ Correct' : 'Enter the correct result to continue.'}</p>
          </div>

          <div className="space-y-3 rounded-input border border-line bg-surface-header p-4">
            <p className="text-xs uppercase tracking-[0.14em] text-txt-muted">Practice 3: Multi-select</p>
            <p className="text-sm text-txt-primary">{config.multiSelectPrompt}</p>
            <p className="text-xs uppercase tracking-[0.14em] text-txt-muted">
              {multiSelectAnswer.length} / {config.multiSelectSelectExactly} selected
            </p>
            <div className="space-y-2">
              {config.multiSelectStatements.map((statement) => {
                const checked = multiSelectAnswer.includes(statement.id);
                const disableUnchecked = !checked && multiSelectAnswer.length >= config.multiSelectSelectExactly;
                return (
                  <label
                    key={statement.id}
                    className={`flex items-start gap-3 rounded-input border px-3 py-2 text-sm ${
                      checked
                        ? 'border-brand-700 bg-surface-card text-txt-primary ring-1 ring-brand-700/20 shadow-sm'
                        : 'border-line bg-surface-card text-txt-secondary hover:border-line-active'
                    } ${disableUnchecked ? 'opacity-60' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disableUnchecked}
                      onChange={(event) => {
                        setMultiSelectAnswer((prev) => {
                          if (event.target.checked) return [...prev, statement.id];
                          return prev.filter((item) => item !== statement.id);
                        });
                      }}
                      className="mt-0.5 h-4 w-4 rounded border-line bg-surface-card text-brand-700 accent-brand-700 focus:ring-brand-700"
                    />
                    <span>{statement.text}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-sm text-txt-muted">{multiSelectCorrect ? '✅ Correct' : 'Select exactly the 2 incorrect statements.'}</p>
          </div>

          <div className="flex justify-end">
            <SimButton disabled={starting || !tutorialComplete} onClick={onStart}>
              {starting
                ? 'Starting...'
                : tutorialComplete
                  ? 'Start simulation'
                  : `Open references: ${referenceTabStatus.opened}/${referenceTabStatus.total}`}
            </SimButton>
          </div>
        </div>
      </SimCard>
    </div>
  );
};

type ReferenceMarkdownSection = {
  title: string;
  content: string;
};

const splitReferenceMarkdownSections = (markdown: string, fallbackTitle: string): ReferenceMarkdownSection[] => {
  const normalized = markdown.replace(/\r\n/g, '\n');
  if (!normalized.trim()) {
    return [{ title: fallbackTitle, content: '' }];
  }

  const lines = normalized.split('\n');
  const sections: ReferenceMarkdownSection[] = [];
  let currentTitle: string | null = null;
  let currentContent: string[] = [];
  let prefaceContent: string[] = [];

  const pushCurrentSection = () => {
    if (!currentTitle) return;
    const joined = currentContent.join('\n').trim();
    sections.push({ title: currentTitle, content: joined });
  };

  for (const line of lines) {
    const headingMatch = line.match(/^###\s+(.+?)\s*$/);
    if (headingMatch) {
      pushCurrentSection();
      currentTitle = headingMatch[1].trim();
      currentContent = [];
      continue;
    }

    if (currentTitle) currentContent.push(line);
    else prefaceContent.push(line);
  }

  pushCurrentSection();

  const preface = prefaceContent.join('\n').trim();
  if (preface) {
    sections.unshift({ title: fallbackTitle, content: preface });
  }

  if (sections.length === 0) {
    return [{ title: fallbackTitle, content: normalized.trim() }];
  }

  return sections;
};

type ReferenceMarkdownProps = {
  markdown: string;
  fallbackTitle: string;
};

type HorizontalOverflowHintProps = {
  children: React.ReactNode;
  className?: string;
  scrollClassName?: string;
};

type ReferenceTabsNavProps = {
  tabs: RuntimeReferenceTab[];
  openedReferenceTabIds: Set<string>;
  activeTabId?: string;
  onSelectTab: (tabId: string) => void;
};

const ReferenceTabsNav: React.FC<ReferenceTabsNavProps> = ({
  tabs,
  openedReferenceTabIds,
  activeTabId,
  onSelectTab,
}) => {
  const tabsScrollRef = useRef<HTMLDivElement | null>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [atLeftEdge, setAtLeftEdge] = useState(true);
  const [atRightEdge, setAtRightEdge] = useState(true);

  const updateOverflowState = useCallback(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 1;
    const atLeft = el.scrollLeft <= 1;
    const atRight = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    setHasOverflow(overflow);
    setAtLeftEdge(atLeft);
    setAtRightEdge(atRight);
  }, []);

  useEffect(() => {
    const el = tabsScrollRef.current;
    if (!el) return;

    updateOverflowState();
    const onScroll = () => updateOverflowState();
    el.addEventListener('scroll', onScroll, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        updateOverflowState();
      });
      observer.observe(el);
      return () => {
        observer.disconnect();
        el.removeEventListener('scroll', onScroll);
      };
    }

    window.addEventListener('resize', updateOverflowState);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateOverflowState);
    };
  }, [updateOverflowState]);

  useEffect(() => {
    updateOverflowState();
  }, [tabs, activeTabId, updateOverflowState]);

  const scrollTabs = (direction: 'left' | 'right') => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const delta = direction === 'left' ? -160 : 160;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      <div ref={tabsScrollRef} className="flex border-b-2 border-line bg-surface-card overflow-x-auto scrollbar-hide">
        {tabs.map((tab) => {
          const opened = openedReferenceTabIds.has(tab.id);
          const dotClass = opened ? 'bg-status-done' : tab.required ? 'bg-status-required' : '';
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onSelectTab(tab.id)}
              className={`
                px-5 py-3.5 text-[13px] font-semibold border-b-2 -mb-[2px]
                whitespace-nowrap flex-shrink-0
                transition-all duration-200
                ${activeTabId === tab.id
                  ? 'text-brand-600 border-brand-600'
                  : 'text-txt-muted border-transparent hover:text-txt-secondary'
                }
              `}
            >
              <span className="inline-flex flex-col items-center leading-tight">
                <span>{tab.title}</span>
                {dotClass ? <span className={`mt-1 inline-block h-[5px] w-[5px] rounded-full ${dotClass}`} aria-hidden /> : null}
              </span>
            </button>
          );
        })}
      </div>

      {hasOverflow && !atLeftEdge ? (
        <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-surface-card to-transparent">
          <div className="pointer-events-auto flex h-full items-center pl-1">
            <button
              type="button"
              onClick={() => scrollTabs('left')}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-line bg-surface-card text-txt-secondary"
              aria-label="Scroll reference tabs left"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : null}

      {hasOverflow && !atRightEdge ? (
        <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-surface-card to-transparent">
          <div className="pointer-events-auto flex h-full items-center justify-end pr-1">
            <button
              type="button"
              onClick={() => scrollTabs('right')}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-line bg-surface-card text-txt-secondary"
              aria-label="Scroll reference tabs right"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const HorizontalOverflowHint: React.FC<HorizontalOverflowHintProps> = ({
  children,
  className = '',
  scrollClassName = '',
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [atLeftEdge, setAtLeftEdge] = useState(true);
  const [atRightEdge, setAtRightEdge] = useState(true);

  const updateOverflowState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 1;
    const atLeft = el.scrollLeft <= 1;
    const atRight = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    setHasOverflow(overflow);
    setAtLeftEdge(atLeft);
    setAtRightEdge(atRight);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateOverflowState();
    const onScroll = () => updateOverflowState();
    el.addEventListener('scroll', onScroll, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        updateOverflowState();
      });
      observer.observe(el);
      if (el.firstElementChild instanceof HTMLElement) {
        observer.observe(el.firstElementChild);
      }
      return () => {
        observer.disconnect();
        el.removeEventListener('scroll', onScroll);
      };
    }

    window.addEventListener('resize', updateOverflowState);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateOverflowState);
    };
  }, [updateOverflowState]);

  useEffect(() => {
    updateOverflowState();
  }, [children, updateOverflowState]);

  return (
    <div className={className}>
      {hasOverflow && !atRightEdge ? (
        <div className="mb-2 flex justify-end">
          <span className="inline-flex items-center gap-1 rounded-full border border-line bg-surface-card/90 px-2.5 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-txt-muted shadow-sm">
            Scroll for more
            <ChevronRight className="h-3 w-3" />
          </span>
        </div>
      ) : null}

      <div className="relative">
        <div ref={scrollRef} className={scrollClassName}>
          {children}
        </div>
        {hasOverflow && !atLeftEdge ? (
          <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-surface-card via-surface-card/85 to-transparent" />
        ) : null}
        {hasOverflow && !atRightEdge ? (
          <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-surface-card via-surface-card/90 to-transparent" />
        ) : null}
      </div>
    </div>
  );
};

const ReferenceMarkdown: React.FC<ReferenceMarkdownProps> = React.memo(({ markdown, fallbackTitle }) => {
  const sections = useMemo(
    () => splitReferenceMarkdownSections(markdown, fallbackTitle),
    [fallbackTitle, markdown]
  );

  return (
    <div className="space-y-8">
      {sections.map((section, index) => {
        const tintClass = REFERENCE_SECTION_TINT_CLASSES[index % REFERENCE_SECTION_TINT_CLASSES.length];
        const Icon = REFERENCE_SECTION_ICONS[index % REFERENCE_SECTION_ICONS.length];
        return (
          <section key={`${section.title}-${index}`} className="space-y-4">
            <div className="flex items-center pb-2 mb-4 border-b border-line-subtle">
              <span className={`w-7 h-7 rounded-icon flex items-center justify-center mr-2.5 text-[14px] ${tintClass}`}>
                <Icon className="h-4 w-4 text-brand-600" />
              </span>
              <h4 className="text-[15px] font-semibold font-body text-txt-primary">{section.title}</h4>
            </div>
            {section.content ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ children }) => <p className="mb-4 text-body text-txt-secondary leading-7 last:mb-0">{children}</p>,
                  strong: ({ children }) => <strong className="font-semibold text-txt-primary">{children}</strong>,
                  h1: ({ children }) => <h1 className="mt-6 mb-3 text-section-title font-display text-txt-primary font-bold">{children}</h1>,
                  h2: ({ children }) => <h2 className="mt-5 mb-3 text-[18px] font-display text-txt-primary font-bold">{children}</h2>,
                  h3: ({ children }) => <h3 className="mt-4 mb-2 text-[15px] font-body text-txt-primary font-semibold">{children}</h3>,
                  h4: ({ children }) => <h4 className="mt-4 mb-2 text-[14px] font-body text-txt-primary font-semibold">{children}</h4>,
                  ul: ({ children }) => <ul className="mb-4 list-disc pl-5 space-y-1 text-txt-secondary">{children}</ul>,
                  ol: ({ children }) => <ol className="mb-4 list-decimal pl-5 space-y-1 text-txt-secondary">{children}</ol>,
                  li: ({ children }) => <li className="text-body text-txt-secondary leading-7">{children}</li>,
                  blockquote: ({ children }) => <blockquote className="mb-4 border-l-2 border-line pl-4 text-body text-txt-secondary">{children}</blockquote>,
                  a: ({ children, href }) => (
                    <a href={href} className="text-txt-link underline decoration-brand-300 hover:text-brand-700" target="_blank" rel="noreferrer">
                      {children}
                    </a>
                  ),
                  code: ({ inline, children }) =>
                    inline ? (
                      <code className="rounded bg-brand-50 px-1.5 py-0.5 font-mono text-[12px] text-brand-700">{children}</code>
                    ) : (
                      <code className="block overflow-x-auto rounded-input bg-surface-input px-3 py-2 font-mono text-[12px] text-txt-primary">
                        {children}
                      </code>
                    ),
                  pre: ({ children }) => <pre className="mb-4">{children}</pre>,
                  table: ({ children }) => (
                    <HorizontalOverflowHint
                      className="-mx-4 px-4 md:mx-0 md:px-0"
                      scrollClassName="overflow-x-auto pb-1"
                    >
                      <div className="min-w-full w-max border border-line rounded-table overflow-hidden bg-surface-card">
                        <table className="w-full min-w-max text-[13px]">{children}</table>
                      </div>
                    </HorizontalOverflowHint>
                  ),
                  thead: ({ children }) => <thead className="bg-surface-header">{children}</thead>,
                  th: ({ children }) => (
                    <th className="px-4 py-3 text-left text-[12px] font-semibold text-txt-primary border-b-2 border-brand-600">
                      {children}
                    </th>
                  ),
                  tbody: ({ children }) => (
                    <tbody className="[&_tr:nth-child(odd)]:bg-surface-card [&_tr:nth-child(even)]:bg-surface-alt [&_tr_td:first-child]:font-medium [&_tr_td:first-child]:text-txt-primary [&_tr_td:not(:first-child)]:text-txt-secondary">
                      {children}
                    </tbody>
                  ),
                  tr: ({ children }) => (
                    <tr className="border-b border-line-subtle transition-colors duration-150 last:border-b-0 hover:bg-surface-hover">
                      {children}
                    </tr>
                  ),
                  td: ({ children }) => <td className="px-4 py-3">{children}</td>,
                }}
              >
                {section.content}
              </ReactMarkdown>
            ) : (
              <p className="text-body text-txt-muted leading-7">No reference content provided.</p>
            )}
          </section>
        );
      })}
    </div>
  );
});

ReferenceMarkdown.displayName = 'ReferenceMarkdown';

const SimAttemptScreenPage: React.FC = () => {
  const navigate = useNavigate();
  const { attemptId, screenCode } = useParams();

  const [runtime, setRuntime] = useState<RuntimeAttemptBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [startingScored, setStartingScored] = useState(false);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [calculatorResetToken, setCalculatorResetToken] = useState('');
  const [referenceDrawerOpen, setReferenceDrawerOpen] = useState(false);
  const [activeReferenceTabId, setActiveReferenceTabId] = useState('');
  const [openedReferenceTabIds, setOpenedReferenceTabIds] = useState<Set<string>>(new Set());
  const [calculatorOpenedForTutorial, setCalculatorOpenedForTutorial] = useState(false);
  const [screenEvents, setScreenEvents] = useState<SimAttemptEvent[]>([]);
  const [screenStartedAt, setScreenStartedAt] = useState<number | null>(null);
  const [timeRemainingSec, setTimeRemainingSec] = useState<number>(0);
  const [autoSubmittedByTimer, setAutoSubmittedByTimer] = useState(false);
  const topAnchorRef = useRef<HTMLDivElement>(null);
  const screenContentRef = useRef<HTMLDivElement>(null);

  const screenEventsRef = useRef<SimAttemptEvent[]>([]);
  const focusStateRef = useRef<boolean | null>(null);
  const idleTimerRef = useRef<number | null>(null);
  const idleStartedAtRef = useRef<number | null>(null);
  const submitInFlightRef = useRef(false);
  const burstWindowRef = useRef<{
    startedAt: number;
    changesCount: number;
    keyPressCount: number;
    reported: boolean;
  } | null>(null);

  const currentScreen = useMemo(() => {
    if (!runtime || !screenCode) return null;
    return runtime.screens.find((item) => item.screenCode === screenCode) || null;
  }, [runtime, screenCode]);
  const screenProgress = useMemo(
    () => computeScreenProgress(runtime?.screens || [], screenCode),
    [runtime?.screens, screenCode]
  );
  const currentProgress = screenProgress.current;
  const totalScreens = screenProgress.total;
  const referenceTabs = useMemo(() => getReferenceTabs(runtime), [runtime]);
  const referenceTabShape = useMemo(() => detectReferenceTabShape(runtime), [runtime]);

  const scoredStartedAtMs = useMemo(() => {
    if (!runtime?.attempt?.scored_started_at) return null;
    const parsed = new Date(runtime.attempt.scored_started_at).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }, [runtime]);

  const isTutorialMode = Boolean(runtime && !scoredStartedAtMs);
  const tutorialConfig = useMemo(() => buildTutorialConfig(runtime), [runtime]);
  const screenTitle = useMemo(
    () => (currentScreen ? formatScreenTitle(currentScreen.screenCode, currentScreen.screenName, currentProgress) : ''),
    [currentScreen, currentProgress]
  );
  const scrollGateSatisfied = useScrollGate(
    screenContentRef,
    isTutorialMode ? null : currentScreen?.screenCode,
    0.9,
    !isTutorialMode
  );

  const simulationIdentityLabel = useMemo(() => {
    if (!runtime) return 'Simulation';
    const baseName = asString(runtime.simulation.roleFamily) || asString(runtime.simulation.name) || 'Simulation';
    return `${baseName} • ${runtime.variant.variantCode}`;
  }, [runtime]);

  const candidateInitials = useMemo(() => {
    const fullName = asString(runtime?.attempt?.watermark?.full_name, '');
    if (!fullName) return 'IV';
    const words = fullName.split(/\s+/).filter(Boolean);
    if (words.length === 0) return 'IV';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
  }, [runtime?.attempt?.watermark?.full_name]);

  const candidateIdLast4 = useMemo(() => asString(runtime?.attempt?.watermark?.sa_id_last4, '----'), [runtime?.attempt?.watermark?.sa_id_last4]);
  const screenSubtitle = useMemo(() => {
    if (!runtime || !currentScreen) return '';
    return `${runtime.simulation.name} • ${runtime.variant.variantCode}`;
  }, [currentScreen, runtime]);

  useEffect(() => {
    if (!screenCode) return;
    let cancelled = false;
    let frame2 = 0;
    let timeoutId = 0;
    const scrollToTop = () => {
      if (cancelled) return;
      topAnchorRef.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
      window.scrollTo({ top: 0, behavior: 'auto' });
      screenContentRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    };
    const frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(scrollToTop);
    });
    timeoutId = window.setTimeout(scrollToTop, 0);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame1);
      if (frame2) window.cancelAnimationFrame(frame2);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [screenCode, scoredStartedAtMs]);

  useEffect(() => {
    if (import.meta.env.VITE_SIM_DEBUG !== '1') return;
    const tabKeys = referenceTabs.map((tab) => tab.id);
    console.debug(`[sim] reference tabs found: ${tabKeys.length} shape=${referenceTabShape}`, tabKeys);
  }, [referenceTabs, referenceTabShape]);

  const activeReferenceTab = useMemo(() => {
    if (!referenceTabs.length) return null;
    return (
      referenceTabs.find((tab) => tab.id === activeReferenceTabId) ||
      referenceTabs[0]
    );
  }, [activeReferenceTabId, referenceTabs]);

  const requiredReferenceTabIds = useMemo(() => referenceTabs.filter((tab) => tab.required).map((tab) => tab.id), [referenceTabs]);

  const referenceTabStatus = useMemo(
    () => computeReferenceTabTutorialStatus(requiredReferenceTabIds, openedReferenceTabIds),
    [openedReferenceTabIds, requiredReferenceTabIds]
  );

  const syncRouteToRuntime = useCallback(
    (bundle: RuntimeAttemptBundle | null | undefined, reason: 'load' | 'start-scored' | 'effect'): boolean => {
      if (!attemptId || !screenCode || !bundle?.attempt?.current_screen_code) return false;
      const runtimeCurrentScreenCode = bundle.attempt.current_screen_code;
      if (screenCode === runtimeCurrentScreenCode) return false;
      if (!bundle.screens.some((item) => item.screenCode === runtimeCurrentScreenCode)) return false;

      const targetUrl = `/sim/attempts/${attemptId}/screens/${runtimeCurrentScreenCode}`;
      if (import.meta.env.DEV || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perf_debug') === '1')) {
        console.info('[SIM_ROUTE_SYNC]', {
          reason,
          attemptId,
          routeScreenCode: screenCode,
          runtimeCurrentScreenCode,
          redirectTo: targetUrl,
        });
      }

      navigate(targetUrl, { replace: true });
      return true;
    },
    [attemptId, navigate, screenCode]
  );

  const handleUnauthorizedSimulationAccess = useCallback(
    (error: any): boolean => {
      const status = error?.response?.status;
      if (status !== 401) return false;

      const message = 'Your session expired. Please reopen your assessment link to continue.';
      if (attemptId) {
        clearAttemptRuntimeCache(attemptId, runtime?.variant.id);
      }
      setError(message);
      setSubmitError(message);
      toast.error(message);
      navigate('/', { replace: true });
      return true;
    },
    [attemptId, navigate, runtime?.variant.id]
  );

  const pushScreenEvent = useCallback((name: string, props?: Record<string, unknown>) => {
    const nextEvent: SimAttemptEvent = {
      ts: Date.now(),
      name,
      props,
    };
    const nextEvents = [...screenEventsRef.current, nextEvent];
    const capped =
      nextEvents.length <= MAX_EVENTS_PER_SCREEN
        ? nextEvents
        : nextEvents.slice(nextEvents.length - MAX_EVENTS_PER_SCREEN);
    screenEventsRef.current = capped;
    setScreenEvents(capped);
  }, []);

  const markReferenceTabOpened = useCallback(
    async (tabId: string) => {
      if (!tabId || !attemptId) return;
      try {
        const response = await api.post(`/sim/attempts/${attemptId}/reference-tabs/opened`, {
          tabKey: tabId,
        });
        const nextOpened = asStringArray(response?.data?.opened_tab_keys);
        if (nextOpened.length > 0) {
          const nextOpenedSet = new Set(nextOpened);
          setOpenedReferenceTabIds(nextOpenedSet);
          const cached = getRuntimeCacheEntryForAttempt(attemptId, runtime?.variant.id);
          if (cached) {
            attemptRuntimeCache.set(cached.key, { ...cached.value, openedTabKeys: nextOpened });
          }
        } else {
          setOpenedReferenceTabIds((prev) => (prev.has(tabId) ? prev : new Set([...prev, tabId])));
        }
        pushScreenEvent('reference_tab_opened', { tabId });
      } catch (error: any) {
        if (handleUnauthorizedSimulationAccess(error)) return;
        // Keep UX non-blocking if persistence fails for now; local interaction still continues.
        toast.error('Unable to save reference tab progress right now.');
      }
    },
    [attemptId, handleUnauthorizedSimulationAccess, pushScreenEvent, runtime?.variant.id]
  );

  const loadRuntime = useCallback(async (): Promise<RuntimeAttemptBundle | null> => {
    if (!attemptId) return null;
    const cached = getRuntimeCacheEntryForAttempt(attemptId, runtime?.variant.id);
    let bundle = cached?.value;
    if (!bundle) {
      const runtimeResponse = await api.get(`/sim/attempts/${attemptId}/runtime`);
      bundle = runtimeResponse.data as RuntimeAttemptBundle;
      setAttemptRuntimeCache(attemptId, bundle.variant.id, bundle);
    }
    const normalizedReferenceTabs = getReferenceTabs(bundle);
    setRuntime(bundle);
    setOpenedReferenceTabIds(new Set(getOpenedTabKeys(bundle)));

    if (normalizedReferenceTabs.length > 0) {
      setActiveReferenceTabId((prev) => (prev && normalizedReferenceTabs.some((tab) => tab.id === prev) ? prev : normalizedReferenceTabs[0].id));
    }

    const screen = bundle.screens.find((item) => item.screenCode === screenCode) || null;
    if (screen) {
      setAnswers({});
      setScreenEvents([]);
      screenEventsRef.current = [];
      setScreenStartedAt(Date.now());
      setCalculatorResetToken(`${attemptId}:${screenCode}`);
      setAutoSubmittedByTimer(false);
    }

    if (!bundle.attempt.scored_started_at) {
      setTimeRemainingSec(bundle.totalTimeSeconds);
      syncRouteToRuntime(bundle, 'load');
      return bundle;
    }

    const startedMs = new Date(bundle.attempt.scored_started_at).getTime();
    setTimeRemainingSec(computeRemainingSeconds(startedMs, bundle.totalTimeSeconds, Date.now()));
    syncRouteToRuntime(bundle, 'load');
    return bundle;
  }, [attemptId, runtime?.variant.id, screenCode, syncRouteToRuntime]);

  useEffect(() => {
    const load = async () => {
      if (!attemptId) {
        setError('Missing attempt id.');
        setLoading(false);
        return;
      }
      if (!screenCode) {
        setError('Missing screen code.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        await loadRuntime();
      } catch (err: any) {
        if (handleUnauthorizedSimulationAccess(err)) return;
        const message = err?.response?.data?.error?.message || 'Failed to load simulation runtime.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [attemptId, screenCode, loadRuntime]);

  useEffect(() => {
    syncRouteToRuntime(runtime, 'effect');
  }, [runtime, syncRouteToRuntime]);

  useEffect(() => {
    if (!runtime || isTutorialMode || !scoredStartedAtMs) return;
    if (timeRemainingSec <= 0 || autoSubmittedByTimer) return;
    const timer = window.setInterval(() => {
      const remaining = computeRemainingSeconds(scoredStartedAtMs, runtime.totalTimeSeconds, Date.now());
      setTimeRemainingSec(remaining);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [autoSubmittedByTimer, isTutorialMode, runtime, scoredStartedAtMs, timeRemainingSec]);

  const requiredTasks = useMemo(() => {
    if (!currentScreen) return [] as RequiredTaskDescriptor[];
    return currentScreen.components
      .map((component) => {
        if (component.rowType !== 'TASK') return null;
        const taskType = typeof component.payload?.taskType === 'string' ? String(component.payload.taskType) : '';
        if (
          !['NumericEntry', 'SingleChoice', 'Picklist', 'MultiSelect', 'MatchingPairs', 'BucketSort'].includes(taskType)
        ) {
          return null;
        }
        const selectExactlyRaw =
          typeof component.payload?.selectExactly === 'number'
            ? component.payload.selectExactly
            : typeof component.payload?.selectExactly === 'string'
              ? Number(component.payload.selectExactly)
              : null;
        const minSelectRaw =
          typeof component.payload?.minSelect === 'number'
            ? component.payload.minSelect
            : typeof component.payload?.minSelect === 'string'
              ? Number(component.payload.minSelect)
              : typeof component.payload?.minSelections === 'number'
                ? component.payload.minSelections
                : typeof component.payload?.minSelections === 'string'
                  ? Number(component.payload.minSelections)
                  : null;
        const pairExactlyRaw =
          typeof component.payload?.pairExactly === 'number'
            ? component.payload.pairExactly
            : typeof component.payload?.pairExactly === 'string'
              ? Number(component.payload.pairExactly)
              : null;
        const pairExactly =
          typeof pairExactlyRaw === 'number' && Number.isInteger(pairExactlyRaw) && pairExactlyRaw > 0
            ? pairExactlyRaw
            : null;
        const matchingLeftItemKeys = normalizeMatchingPairLeftItemKeys(component.payload);
        const bucketItemKeys = normalizeBucketSortItemKeys(component.payload);
        const bucketKeys = normalizeBucketSortBucketKeys(component.payload);
        const requireAllAssigned =
          typeof component.payload?.requireAllAssigned === 'boolean' ? component.payload.requireAllAssigned : true;
        return {
          rowKey: component.rowKey,
          taskType,
          selectExactly:
            typeof selectExactlyRaw === 'number' && Number.isInteger(selectExactlyRaw) && selectExactlyRaw > 0
              ? selectExactlyRaw
              : null,
          minSelect:
            typeof minSelectRaw === 'number' && Number.isInteger(minSelectRaw) && minSelectRaw > 0
              ? minSelectRaw
              : null,
          pairExactly,
          matchingLeftItemKeys,
          bucketItemKeys,
          bucketKeys,
          requireAllAssigned,
        };
      })
      .filter(
        (item): item is RequiredTaskDescriptor => Boolean(item)
      );
  }, [currentScreen]);

  const hasMissingRequiredAnswers = useMemo(() => {
    return hasMissingRequiredTaskAnswers(requiredTasks, answers, requiredTasks.length > 0);
  }, [answers, requiredTasks]);

  const actionDisabled =
    submitting ||
    isTutorialMode ||
    timeRemainingSec <= 0 ||
    (!isTutorialMode && !scrollGateSatisfied) ||
    (requiredTasks.length > 0 && hasMissingRequiredAnswers);

  const markActivity = useCallback(
    (kind: 'change' | 'keypress' | 'pointer') => {
      if (!runtime || !currentScreen || isTutorialMode) return;
      const now = Date.now();

      if (idleStartedAtRef.current !== null) {
        pushScreenEvent('IDLE_END', { idle_ms: now - idleStartedAtRef.current });
        idleStartedAtRef.current = null;
        burstWindowRef.current = {
          startedAt: now,
          changesCount: 0,
          keyPressCount: 0,
          reported: false,
        };
      }

      const windowState = burstWindowRef.current;
      if (windowState) {
        const elapsed = now - windowState.startedAt;
        if (elapsed <= BURST_WINDOW_MS) {
          if (kind === 'change') windowState.changesCount += 1;
          if (kind === 'keypress') windowState.keyPressCount += 1;
          if (!windowState.reported && (windowState.changesCount >= 3 || windowState.keyPressCount >= 10)) {
            windowState.reported = true;
            pushScreenEvent('BURST_AFTER_IDLE', {
              window_ms: elapsed,
              changes_count: windowState.changesCount,
            });
          }
        }
      }

      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = window.setTimeout(() => {
        if (idleStartedAtRef.current === null) {
          idleStartedAtRef.current = Date.now();
          pushScreenEvent('IDLE_START');
        }
      }, IDLE_THRESHOLD_MS);
    },
    [currentScreen, isTutorialMode, pushScreenEvent, runtime]
  );

  useEffect(() => {
    if (!runtime || !currentScreen || isTutorialMode) return;

    const onKeyDown = (event: KeyboardEvent) => {
      markActivity('keypress');
      if (event.key !== 'Backspace') return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase() || '';
      const isEditable =
        tag === 'input' || tag === 'textarea' || Boolean((target as HTMLElement | null)?.isContentEditable);
      if (!isEditable) {
        pushScreenEvent('BACK_NAV_ATTEMPT', { source: 'backspace' });
      }
    };

    const onPointer = () => markActivity('pointer');
    const onVisibility = () => {
      const isFocused = document.visibilityState !== 'hidden' && document.hasFocus();
      const prev = focusStateRef.current;
      if (prev === isFocused) return;
      focusStateRef.current = isFocused;
      pushScreenEvent(isFocused ? 'FOCUS_GAINED' : 'FOCUS_LOST', {
        source: 'visibilitychange',
      });
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousemove', onPointer);
    window.addEventListener('scroll', onPointer, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousemove', onPointer);
      window.removeEventListener('scroll', onPointer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [currentScreen, isTutorialMode, markActivity, pushScreenEvent, runtime]);

  const handleInputActivity = useCallback(
    (activity: { type: 'paste' | 'change'; rowKey: string; inputType: 'NumericEntry' | 'other' }) => {
      if (activity.type === 'change') {
        markActivity('change');
        return;
      }
      pushScreenEvent('PASTE_DETECTED', {
        fieldKey: activity.rowKey,
        inputType: activity.inputType,
        screenCode,
      });
    },
    [markActivity, pushScreenEvent, screenCode]
  );

  const clearEventBuffer = useCallback(() => {
    screenEventsRef.current = [];
    setScreenEvents([]);
  }, []);

  const handleSubmitCurrentScreen = useCallback(async () => {
    if (!attemptId || !screenCode || !runtime || !currentScreen || isTutorialMode) return;
    if (submitInFlightRef.current) {
      if (import.meta.env.DEV || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perf_debug') === '1')) {
        console.info('[SIM_SUBMIT_DUPLICATE_BLOCKED]', {
          attemptId,
          routeScreenCode: screenCode,
        });
      }
      return;
    }

    submitInFlightRef.current = true;
    setSubmitError(null);
    setSubmitting(true);

    const submitTs = Date.now();
    const clientMeta: Record<string, number> = {
      screen_submitted_at: submitTs,
    };
    if (screenStartedAt) clientMeta.screen_started_at = screenStartedAt;

    let eventsToSubmit = [...screenEventsRef.current];
    if (idleStartedAtRef.current !== null) {
      eventsToSubmit = [
        ...eventsToSubmit,
        {
          ts: submitTs,
          name: 'IDLE_END',
          props: { idle_ms: submitTs - idleStartedAtRef.current },
        },
      ];
      idleStartedAtRef.current = null;
    }

    try {
      if (import.meta.env.DEV || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perf_debug') === '1')) {
        console.info('[SIM_SUBMIT_START]', {
          attemptId,
          routeScreenCode: screenCode,
          eventCount: eventsToSubmit.length,
        });
      }
      const response = await api.post<SubmitScreenResponse>(`/sim/attempts/${attemptId}/screens/${screenCode}/submit`, {
        answers,
        events: eventsToSubmit,
        client_meta: clientMeta,
      });

      if (import.meta.env.DEV || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perf_debug') === '1')) {
        console.info('[SIM_SUBMIT_SUCCESS]', {
          attemptId,
          routeScreenCode: screenCode,
          nextUrl: response.data?.next_url || null,
          status: response.data?.status || null,
          alreadySubmitted: response.data?.already_submitted || false,
        });
      }

      if (response.data?.status === 'EXPIRED' || response.data?.expired === true) {
        toast.error('Time expired for this assessment.');
        clearAttemptRuntimeCache(attemptId, runtime?.variant.id);
        navigate('/');
        return;
      }

      clearEventBuffer();
      setSubmitError(null);

      if (response.data?.next_url) {
        clearAttemptRuntimeCache(attemptId, runtime?.variant.id);
        navigate(response.data.next_url, { replace: true });
        return;
      }

      if (response.data?.status === 'COMPLETED') {
        clearAttemptRuntimeCache(attemptId, runtime?.variant.id);
        toast.success('Assessment completed');
        navigate('/', { replace: true });
        return;
      }

      toast.error('Submission succeeded but no next screen was provided. Please exit and retry.');
    } catch (err: any) {
      if (handleUnauthorizedSimulationAccess(err)) return;
      const message = err?.response?.data?.error?.message || 'Unable to submit this screen.';
      setSubmitError(message);
      toast.error(message);
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  }, [
    answers,
    attemptId,
    clearEventBuffer,
    currentScreen,
    handleUnauthorizedSimulationAccess,
    isTutorialMode,
    navigate,
    runtime,
    screenCode,
    screenStartedAt,
  ]);

  useEffect(() => {
    if (!runtime || isTutorialMode || submitting || autoSubmittedByTimer) return;
    if (timeRemainingSec > 0) return;
    setAutoSubmittedByTimer(true);
    void handleSubmitCurrentScreen();
  }, [autoSubmittedByTimer, handleSubmitCurrentScreen, isTutorialMode, runtime, submitting, timeRemainingSec]);

  const handleToggleCalculator = () => {
    setCalculatorOpen((wasOpen) => {
      const nextOpen = !wasOpen;
      if (nextOpen) {
        setCalculatorOpenedForTutorial(true);
        pushScreenEvent('calculator_opened');
      } else {
        pushScreenEvent('calculator_closed');
      }
      return nextOpen;
    });
  };

  const handleCloseReferenceDrawer = () => {
    setReferenceDrawerOpen(false);
  };

  const handleOpenReferenceDrawer = () => {
    if (!referenceTabs.length) return;
    setReferenceDrawerOpen(true);
    if (activeReferenceTab?.id) {
      void markReferenceTabOpened(activeReferenceTab.id);
    }
  };

  const handleSelectReferenceTab = (tabId: string) => {
    setActiveReferenceTabId(tabId);
    void markReferenceTabOpened(tabId);
    const isMobile = !window.matchMedia('(min-width: 768px)').matches;
    if (isMobile) setReferenceDrawerOpen(false);
  };

  const openReferencesProgressLabel = `Open references: ${referenceTabStatus.opened}/${referenceTabStatus.total}`;

  const handleStartScored = async () => {
    if (!attemptId) return;

    if (!referenceTabStatus.satisfiedForCompletion) {
      toast.error(openReferencesProgressLabel);
      return;
    }

    setStartingScored(true);
    try {
      const response = await api.post(`/sim/attempts/${attemptId}/start-scored`);
      if (import.meta.env.DEV || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('perf_debug') === '1')) {
        console.info('[SIM_START_SCORED]', {
          attemptId,
          routeScreenCode: screenCode,
          response: response.data,
        });
      }
      clearAttemptRuntimeCache(attemptId, runtime?.variant.id);
      const bundle = await loadRuntime();
      syncRouteToRuntime(bundle, 'start-scored');
      toast.success('Scored assessment started — timer is now running.', {
        duration: 3000,
      });
    } catch (err: any) {
      if (handleUnauthorizedSimulationAccess(err)) return;
      const message = err?.response?.data?.error?.message || 'Could not start simulation.';
      toast.error(message);
    } finally {
      setStartingScored(false);
    }
  };

  const canShowCalculator = isTutorialMode || Boolean(currentScreen?.ui?.show_calculator);

  if (loading) return <SimScreenSkeleton />;

  if (error || !runtime || !currentScreen) {
    return (
      <SimShell maxWidthClassName="max-w-3xl" className="flex items-center">
        <SimCard className="space-y-4 text-center">
          <SimLabel className="text-danger">Error</SimLabel>
          <SimTitle className="text-xl sm:text-2xl">Couldn’t load this screen</SimTitle>
          <SimBody>{error || 'Please refresh and try again.'}</SimBody>
          <div className="flex justify-center">
            <SimButton onClick={() => navigate('/')}>Exit</SimButton>
          </div>
        </SimCard>
      </SimShell>
    );
  }

  return (
    <SimShell
      maxWidthClassName="max-w-[1520px]"
      className="px-0 pb-0"
      contentClassName="space-y-[22px] p-7"
      mainTopClassName="mt-0"
      headerFullWidth
      headerUnwrapped
      dynamicHeaderOffset
      headerInnerClassName=""
      headerClassName="relative z-40"
      fixedAction={
        !isTutorialMode ? (
          <SimButton onClick={handleSubmitCurrentScreen} disabled={actionDisabled} className="w-full sm:w-auto">
            {submitting
              ? 'Submitting...'
              : currentProgress === totalScreens
                ? 'Submit'
                : 'Next'}
          </SimButton>
        ) : undefined
      }
      header={
        <div>
          <div
            data-iv-header-line="1"
            className="fixed inset-x-0 top-0 z-[200] w-full border-b border-line-subtle bg-surface-card px-6 py-[10px] shadow-themebar"
          >
            <div className="flex items-center justify-between gap-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-txt-muted">{simulationIdentityLabel}</p>
              <div className="flex items-center gap-2">
                {canShowCalculator ? (
                  <button
                    type="button"
                    onClick={handleToggleCalculator}
                    className="iv-theme-btn iv-theme-btn-active"
                    aria-label="Open calculator"
                  >
                    <Calculator className="h-4 w-4 text-txt-inverse" />
                    Calc
                  </button>
                ) : null}
                <span className="inline-flex items-center rounded-full border border-line bg-surface-card px-3 py-1 text-xs uppercase tracking-[0.2em] text-txt-secondary">
                  {!isTutorialMode
                    ? `${String(Math.floor(timeRemainingSec / 60)).padStart(2, '0')}:${String(timeRemainingSec % 60).padStart(2, '0')}`
                    : '--:--'}
                </span>
              </div>
            </div>
          </div>
          <div
            data-iv-header-line="2"
            className="iv-line2-top fixed inset-x-0 z-[150] w-full min-h-[64px] border-b border-line bg-surface-headerbar px-7 py-[14px] backdrop-blur-[12px]"
          >
            <div ref={topAnchorRef} className="h-0 w-0 overflow-hidden" aria-hidden />
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-[14px]">
                <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-brand-logo text-[15px] font-bold text-txt-inverse">
                  I
                </div>
                <div className="flex items-center">
                  <p className="text-[15px] font-bold text-txt-primary">Intellerview</p>
                  <p className="ml-2 text-[12px] text-txt-muted">Simulation Assessment</p>
                </div>
              </div>
              <div className="flex items-center gap-[14px]">
                <p className="text-[12px] text-txt-muted">
                  Candidate <span className="font-semibold text-txt-primary">{runtime.attempt.watermark.full_name}</span> • ID: {candidateIdLast4}
                </p>
                <span className="flex h-9 w-9 items-center justify-center rounded-avatar bg-brand-50 text-[11px] font-bold text-brand-700">
                  {candidateInitials}
                </span>
              </div>
            </div>
          </div>
        </div>
      }
    >
      <div>
        {isTutorialMode ? (
          <div className="space-y-2">
            <p className="text-overline font-bold text-brand-600">Tutorial</p>
            <h1 className="text-page-title font-display font-bold text-txt-primary">Practice Before You Start</h1>
            <p className="text-small text-txt-secondary">{tutorialConfig.introMarkdown}</p>
            <p className="text-small text-txt-secondary">
              Take your time here — this step isn&apos;t timed. Read through the reference tabs now to orient yourself before
              you start.
            </p>
          </div>
        ) : (
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-page-title font-display font-bold text-txt-primary">{screenTitle}</h1>
              <p className="mt-1 text-small text-txt-secondary">{screenSubtitle}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-brand-700">
                Screen {currentProgress} of {totalScreens}
              </span>
              {referenceTabs.length > 0 ? (
                <button
                  type="button"
                  onClick={handleOpenReferenceDrawer}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-button border border-line bg-surface-card px-4 py-2.5 text-[13px] font-medium text-txt-secondary transition-colors hover:bg-surface-alt md:hidden"
                  aria-label="Open references"
                >
                  <BookOpenText className="h-4 w-4 text-brand-600" />
                  References
                </button>
              ) : null}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-[22px] md:flex-row md:items-start md:justify-center">
        <div className="min-w-0 flex-1">
          {isTutorialMode ? (
            <TutorialPanel
              config={tutorialConfig}
              calculatorOpened={calculatorOpenedForTutorial}
              referenceTabIds={requiredReferenceTabIds}
              openedReferenceTabIds={openedReferenceTabIds}
              onStart={handleStartScored}
              starting={startingScored}
            />
          ) : (
            <SimCard className="border-0 bg-transparent p-0 shadow-none">
              <div
                ref={screenContentRef}
                className="space-y-4 pr-2"
              >
                <div className={`p-0 ${timeRemainingSec <= 0 ? 'pointer-events-none opacity-70' : ''}`}>
                  <ScreenRenderer
                    components={currentScreen.components}
                    answers={answers}
                    onAnswersChange={(nextAnswers) => {
                      setSubmitError(null);
                      setAnswers((prev) => ({ ...prev, ...nextAnswers }));
                    }}
                    onInputActivity={handleInputActivity}
                  />
                </div>
                {timeRemainingSec <= 0 ? (
                  <div className="rounded-input border border-danger/30 bg-danger-light px-4 py-3 text-sm text-danger-text">
                    Time is up. Submitting your current screen.
                  </div>
                ) : null}
                {submitError ? (
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-input border border-danger/30 bg-danger-light px-4 py-3 text-sm text-danger-text">
                    <span>{submitError}</span>
                    <button
                      type="button"
                      onClick={() => {
                        void handleSubmitCurrentScreen();
                      }}
                      className="rounded border border-danger/30 px-3 py-1 text-xs uppercase tracking-[0.14em] text-danger-text transition-colors hover:bg-danger-light"
                    >
                      Retry
                    </button>
                  </div>
                ) : null}
              </div>
            </SimCard>
          )}
        </div>
        {referenceTabs.length > 0 ? (
          <aside
            className="hidden md:flex md:w-[540px] xl:w-[580px] md:flex-shrink-0 md:flex-col md:overflow-hidden md:rounded-card md:border md:border-line md:bg-surface-page/70 lg:sticky lg:top-[calc(var(--iv-header-stack,116px)+1rem)] lg:max-h-[calc(100vh-var(--iv-header-stack,116px)-1.5rem)]"
          >
            <div className="flex min-h-0 flex-1 flex-col">
              <ReferenceTabsNav
                tabs={referenceTabs}
                openedReferenceTabIds={openedReferenceTabIds}
                activeTabId={activeReferenceTab?.id}
                onSelectTab={handleSelectReferenceTab}
              />
              {activeReferenceTab ? (
                <div className="flex min-h-0 flex-1 flex-col bg-surface-card border border-line border-t-0 rounded-b-card overflow-hidden">
                  <div className="flex flex-shrink-0 items-center justify-between border-b-2 border-brand-600 px-7 py-5">
                    <h3 className="text-section-title font-display text-txt-primary font-bold">{activeReferenceTab.title}</h3>
                    {activeReferenceTab.required ? (
                      <span className="text-overline px-2.5 py-1 rounded-badge bg-brand-50 text-brand-600">
                        Required Reading
                      </span>
                    ) : null}
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto px-7 py-6">
                    <ReferenceMarkdown
                      markdown={activeReferenceTab.contentMarkdown}
                      fallbackTitle={activeReferenceTab.title}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </aside>
        ) : null}
      </div>

      {referenceDrawerOpen ? (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div className="absolute inset-0 bg-surface-page/80" onClick={handleCloseReferenceDrawer} />
          <div className="relative ml-auto flex h-full w-[92vw] max-w-[28rem] flex-col border-l border-line bg-surface-page/95">
            <div className="flex items-center justify-between border-b border-line px-3 py-3">
              <p className="text-sm font-semibold text-txt-primary">References</p>
              <button
                type="button"
                onClick={handleCloseReferenceDrawer}
                className="inline-flex h-8 w-8 items-center justify-center rounded-input border border-line text-txt-secondary"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <ReferenceTabsNav
              tabs={referenceTabs}
              openedReferenceTabIds={openedReferenceTabIds}
              activeTabId={activeReferenceTab?.id}
              onSelectTab={handleSelectReferenceTab}
            />
            {activeReferenceTab ? (
              <div className="bg-surface-card border border-line border-t-0 rounded-b-card overflow-hidden">
                <div className="px-7 py-5 border-b-2 border-brand-600 flex items-center justify-between">
                  <h3 className="text-section-title font-display text-txt-primary font-bold">{activeReferenceTab.title}</h3>
                  {activeReferenceTab.required ? (
                    <span className="text-overline px-2.5 py-1 rounded-badge bg-brand-50 text-brand-600">
                      Required Reading
                    </span>
                  ) : null}
                </div>
                <div className="px-7 py-6 max-h-[calc(100vh-220px)] overflow-y-auto">
                  <ReferenceMarkdown markdown={activeReferenceTab.contentMarkdown} fallbackTitle={activeReferenceTab.title} />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <SimCalculator
        isOpen={calculatorOpen}
        onClose={() => {
          setCalculatorOpen(false);
          pushScreenEvent('calculator_closed');
        }}
        resetToken={calculatorResetToken}
        positionStorageKey={`sim_calc_pos:${attemptId || 'default'}`}
        onCalculatorEvent={(eventName) => {
          if (eventName === 'calculator_opened') setCalculatorOpenedForTutorial(true);
          pushScreenEvent(eventName);
        }}
      />

    </SimShell>
  );
};

export default SimAttemptScreenPage;
